import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "@/lib/api-client";
import { PageTitleBar } from "@/components/ui/PageTitleBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useAuthLite } from "@/context/AuthLite";
import { toast } from "@/hooks/use-toast";

type LossReasonCategory = {
  id: string;
  key: string;
  name: string;
  description: string;
  count: number;
};

type LossReasonItem = {
  id: string;
  title: string | null;
  category_id: string;
  confidence: number | null;
  classified_at: string | null;
  note_id: string | null;
  note_text: string | null;
};

type LossReasonsResponse = {
  categories: LossReasonCategory[];
  items: LossReasonItem[];
};

function confidenceLabel(conf: number | null): { label: string; className: string } {
  if (conf === null || !Number.isFinite(conf)) return { label: "—", className: "text-muted-foreground" };
  if (conf >= 80) return { label: `${conf}%`, className: "text-emerald-600 dark:text-emerald-400" };
  if (conf >= 60) return { label: `${conf}%`, className: "text-amber-600 dark:text-amber-400" };
  return { label: `${conf}%`, className: "text-red-600 dark:text-red-400" };
}

export default function LossReasonsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isLoading: authLoading, authMode } = useAuthLite();
  const [categoryId, setCategoryId] = useState<string>("all");
  const [minConfidence, setMinConfidence] = useState<string>("0");

  const separateApiOriginDev =
    import.meta.env.DEV &&
    !!import.meta.env.VITE_API_BASE_URL &&
    (() => {
      try {
        return new URL(import.meta.env.VITE_API_BASE_URL).origin !== window.location.origin;
      } catch {
        return true;
      }
    })();

  const canBootstrap =
    authMode === "email" ? !!user && user.role === "Admin" && !authLoading : !authLoading;

  const q = useQuery({
    queryKey: ["loss-reasons"],
    queryFn: async (): Promise<LossReasonsResponse> => {
      const res = await apiFetch("/api/crm/loss-reasons");
      if (!res.ok) throw new Error(await res.text());
      return (await res.json()) as LossReasonsResponse;
    },
  });

  const bootstrap = useMutation({
    mutationFn: async () => {
      const res = await apiFetch("/api/crm/loss-reasons/bootstrap", { method: "POST" });
      const text = await res.text();
      if (!res.ok) {
        let errObj: any = null;
        try {
          errObj = JSON.parse(text);
        } catch {
          errObj = null;
        }
        const code = typeof errObj?.error === "string" ? errObj.error : null;
        const msg = typeof errObj?.message === "string" ? errObj.message : null;
        if (res.status === 401 || code === "unauthorized") {
          throw new Error(
            t(
              "lossReasons.bootstrapUnauthorized",
              "Bejelentkezés szükséges: az API nem kapta meg az `auth` sütit (401 unauthorized).",
            ),
          );
        }
        if (res.status === 403 && code === "admin_only") {
          throw new Error(t("lossReasons.bootstrapAdminOnly", "Csak Admin futtathatja ezt a műveletet."));
        }
        if (res.status === 422 && code === "migration_needed") {
          throw new Error(msg || t("lossReasons.bootstrapMigrationNeeded", "Hiányzó DB migráció — futtasd a 142_loss_reason_categories migrációt."));
        }
        throw new Error(text);
      }
      let parsed: any = null;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = { raw: text };
      }
      return parsed;
    },
    onSuccess: async (data) => {
      const classified = typeof data?.classified === "number" ? data.classified : null;
      const derived = typeof data?.derived === "boolean" ? data.derived : null;
      const message = typeof data?.message === "string" ? data.message : "";
      toast({
        title: t("lossReasons.bootstrapDoneTitle", "Újrafuttatás kész"),
        description:
          classified !== null
            ? t("lossReasons.bootstrapDoneDesc", "Besorolt: {{count}}", { count: classified }) +
                (derived ? ` · ${t("lossReasons.bootstrapDerived", "kategóriák generálva")}` : "") +
                (message ? ` · ${message}` : "")
            : t("lossReasons.bootstrapDoneGeneric", "Válasz feldolgozva."),
      });
      await q.refetch();
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : String(err);
      const isUnauthorized =
        msg.includes("401") ||
        msg.toLowerCase().includes("unauthorized") ||
        msg.includes(t("lossReasons.bootstrapUnauthorized", "Bejelentkezés szükséges"));
      toast({
        variant: "destructive",
        title: t("lossReasons.bootstrapFailedTitle", "Újrafuttatás sikertelen"),
        description: msg,
        action: isUnauthorized ? (
          <Button size="sm" variant="secondary" onClick={() => navigate("/login")}>
            {t("auth.login", "Bejelentkezés")}
          </Button>
        ) : undefined,
      });
    },
  });

  const minConfN = Math.max(0, Math.min(100, parseInt(minConfidence || "0", 10) || 0));

  const filtered = useMemo(() => {
    const items = q.data?.items ?? [];
    return items.filter((it) => {
      if (categoryId !== "all" && it.category_id !== categoryId) return false;
      const c = typeof it.confidence === "number" ? it.confidence : 0;
      if (c < minConfN) return false;
      return true;
    });
  }, [q.data, categoryId, minConfN]);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="relative z-20 h-[60px] flex-shrink-0 overflow-hidden border-b bg-background">
        <PageTitleBar
          className="h-full border-0"
          rowClassName="h-full min-h-0 items-center"
          title={t("lossReasons.title", "Loss reason analýza")}
          right={
            <Button
              variant="outline"
              disabled={bootstrap.isPending || !canBootstrap || separateApiOriginDev}
              onClick={() => bootstrap.mutate()}
              className="h-9"
              title={t("lossReasons.bootstrapHelp", "Újrafuttatás: csak a még nem besorolt Lost ügyleteket kategorizálja.")}
            >
              {bootstrap.isPending ? t("common.loading", "Töltés…") : t("lossReasons.bootstrap", "Újrafuttatás (hiányzók)")}
            </Button>
          }
        />
      </div>

      <div className="min-h-0 flex-1 min-w-0 overflow-auto p-3 sm:p-4">
        {q.isError ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            {String(q.error)}
          </div>
        ) : null}

        {separateApiOriginDev ? (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-950 dark:text-amber-100">
            {t(
              "lossReasons.separateApiOriginDev",
              "Fejlesztői figyelmeztetés: a `VITE_API_BASE_URL` más originre mutat, ezért a böngésző gyakran nem küldi az `auth` sütit — az admin POST hívások 401-et kaphatnak. Hagyd üresen lokálisan (Vite proxy `/api` → :8788), vagy futtasd a `pnpm dev:full` módot.",
            )}
          </div>
        ) : null}

        {!authLoading && authMode === "email" && !user ? (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-950 dark:text-amber-100">
            {t(
              "lossReasons.notSignedIn",
              "Nincs aktív bejelentkezés — az újrafuttatás admin API hívás auth sütit igényel.",
            )}{" "}
            <Button size="sm" variant="secondary" className="ml-2 h-8" onClick={() => navigate("/login")}>
              {t("auth.login", "Bejelentkezés")}
            </Button>
          </div>
        ) : null}

        {!authLoading && authMode === "email" && user && user.role !== "Admin" ? (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-950 dark:text-amber-100">
            {t("lossReasons.notAdmin", "Ez az oldal admin műveletet tartalmaz; a fiókod nem Admin szerepkörű.")}
          </div>
        ) : null}

        <div className="grid gap-3 md:grid-cols-[1fr,320px]">
          <div className="rounded-lg border bg-card">
            <div className="border-b px-4 py-3">
              <div className="text-sm font-semibold">{t("lossReasons.summary", "Összesítés")}</div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <label className="text-xs text-muted-foreground">{t("lossReasons.filterCategory", "Kategória")}</label>
                <select
                  className="h-9 rounded-md border bg-background px-2 text-sm"
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                >
                  <option value="all">{t("lossReasons.all", "Összes")}</option>
                  {(q.data?.categories ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.count})
                    </option>
                  ))}
                </select>

                <label className="ml-2 text-xs text-muted-foreground">{t("lossReasons.minConfidence", "Min. bizalom")}</label>
                <Input
                  value={minConfidence}
                  onChange={(e) => setMinConfidence(e.target.value)}
                  className="h-9 w-[90px]"
                  inputMode="numeric"
                />
              </div>
            </div>

            <div className="p-4">
              <div className="space-y-2">
                {(q.data?.categories ?? []).map((c, idx, arr) => {
                  const max = Math.max(1, ...arr.map((x) => x.count));
                  const w = Math.round((c.count / max) * 100);
                  return (
                    <div key={c.id} className="grid grid-cols-[1fr,72px] items-center gap-3">
                      <div>
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm font-medium">{c.name}</div>
                          <div className="text-xs text-muted-foreground">{c.count}</div>
                        </div>
                        <div className="mt-1 h-2 overflow-hidden rounded bg-muted">
                          <div className="h-full bg-primary" style={{ width: `${w}%` }} />
                        </div>
                      </div>
                      <div className="text-right text-xs text-muted-foreground">{c.key}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="rounded-lg border bg-card">
            <div className="border-b px-4 py-3">
              <div className="text-sm font-semibold">{t("lossReasons.legend", "Kategóriák")}</div>
              <div className="text-xs text-muted-foreground">
                {t("lossReasons.legendHelp", "A kategóriákat a Closed Lost ügyletek legutolsó jegyzeteiből generáljuk.")}
              </div>
            </div>
            <div className="space-y-3 p-4">
              {(q.data?.categories ?? []).map((c) => (
                <div key={c.id} className="rounded-md border bg-background px-3 py-2">
                  <div className="text-sm font-semibold">{c.name}</div>
                  <div className="text-xs text-muted-foreground">{c.description}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4 rounded-lg border bg-card">
          <div className="border-b px-4 py-3">
            <div className="text-sm font-semibold">{t("lossReasons.deals", "Érintett ügyletek")}</div>
            <div className="text-xs text-muted-foreground">
              {t("lossReasons.dealsHelp", "Táblázat: kategória, bizalom, és a besoroláshoz használt legutolsó jegyzet.")}
            </div>
          </div>

          <div className="overflow-auto">
            <table className="w-full min-w-[860px] text-left text-sm">
              <thead className="sticky top-0 bg-card">
                <tr className="border-b">
                  <th className="px-4 py-2">{t("lossReasons.colDeal", "Ügylet")}</th>
                  <th className="px-4 py-2">{t("lossReasons.colCategory", "Kategória")}</th>
                  <th className="px-4 py-2">{t("lossReasons.colConfidence", "Bizalom")}</th>
                  <th className="px-4 py-2">{t("lossReasons.colNote", "Jegyzet")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => {
                  const conf = confidenceLabel(it.confidence);
                  const cat = (q.data?.categories ?? []).find((c) => c.id === it.category_id);
                  return (
                    <tr key={it.id} className="border-b last:border-b-0">
                      <td className="px-4 py-3">
                        <div className="font-medium">{it.title ?? it.id}</div>
                        <div className="text-xs text-muted-foreground">{it.id}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{cat?.name ?? it.category_id}</div>
                        <div className="text-xs text-muted-foreground">{cat?.key ?? ""}</div>
                      </td>
                      <td className={cn("px-4 py-3 font-semibold", conf.className)}>{conf.label}</td>
                      <td className="px-4 py-3">
                        <div className="line-clamp-3 whitespace-pre-wrap text-xs text-muted-foreground">
                          {it.note_text ?? "—"}
                        </div>
                        {it.note_id ? (
                          <div className="mt-1 text-[11px] text-muted-foreground/70">note: {it.note_id}</div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-sm text-muted-foreground">
                      {q.isLoading ? t("common.loading", "Töltés…") : t("lossReasons.empty", "Nincs megjeleníthető sor.")}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

